import React, { useState } from 'react';
import { AlertCircle, CheckCircle, Eye, EyeOff } from 'lucide-react';

/**
 * Composant réutilisable pour les champs de formulaire
 * Gère la validation, les erreurs et le feedback
 */
export const FormField = ({
  id,
  name,
  label,
  type = 'text',
  value,
  onChange,
  onBlur,
  error,
  touched,
  placeholder,
  required = false,
  disabled = false,
  autoComplete,
  className = '',
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const hasError = touched && error;
  const fieldName = name || id;
  const isPasswordField = type === 'password';
  const inputType = isPasswordField && showPassword ? 'text' : type;

  return (
    <div className="flex flex-col gap-2">
      {label && (
        <label htmlFor={id} className="text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-600 ml-1">*</span>}
        </label>
      )}
      
      <div className="relative">
        <input
          id={id}
          name={fieldName}
          type={inputType}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete={autoComplete}
          aria-invalid={hasError}
          aria-describedby={hasError ? `${id}-error` : undefined}
          className={`
            w-full px-4 py-2.5 border rounded-lg
            focus:outline-none focus:ring-2 focus:ring-offset-2
            transition-colors duration-200
            ${isPasswordField ? 'pr-12' : ''}
            ${hasError 
              ? 'border-red-500 bg-red-50 focus:ring-red-500' 
              : 'border-gray-300 focus:border-teal-500 focus:ring-teal-500'
            }
            ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}
            ${className}
          `}
        />
        
        {isPasswordField && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-3 text-gray-500 hover:text-gray-700 transition"
            aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
          >
            {showPassword ? (
              <EyeOff className="w-5 h-5" />
            ) : (
              <Eye className="w-5 h-5" />
            )}
          </button>
        )}
        
        {touched && !error && value && !isPasswordField && (
          <CheckCircle className="absolute right-3 top-3 text-green-500 w-5 h-5" />
        )}
      </div>

      {hasError && (
        <div className="flex items-center gap-2 text-sm text-red-600">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span id={`${id}-error`} role="alert">
            {error}
          </span>
        </div>
      )}
    </div>
  );
};

/**
 * Composant pour les messages d'alerte
 */
export const AlertMessage = ({ type = 'error', message, onClose }) => {
  if (!message) return null;

  const bgColor = {
    error: 'bg-red-50 border-red-200 text-red-800',
    success: 'bg-green-50 border-green-200 text-green-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800',
    warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
  }[type];

  const iconColor = {
    error: 'text-red-500',
    success: 'text-green-500',
    info: 'text-blue-500',
    warning: 'text-yellow-500',
  }[type];

  return (
    <div
      role="alert"
      className={`border rounded-lg p-4 flex items-start gap-3 ${bgColor}`}
    >
      {type === 'error' && <AlertCircle className={`w-5 h-5 flex-shrink-0 ${iconColor}`} />}
      {type === 'success' && <CheckCircle className={`w-5 h-5 flex-shrink-0 ${iconColor}`} />}
      
      <div className="flex-1">
        <p className="text-sm font-medium">{message}</p>
      </div>

      {onClose && (
        <button
          onClick={onClose}
          className="text-lg font-bold opacity-70 hover:opacity-100 focus:outline-none"
          aria-label="Fermer"
        >
          ×
        </button>
      )}
    </div>
  );
};

/**
 * Composant pour les boutons de formulaire
 */
export const FormButton = ({
  children,
  isLoading = false,
  disabled = false,
  type = 'button',
  variant = 'primary',
  className = '',
  ...props
}) => {
  const baseClasses = 'w-full px-4 py-2.5 rounded-lg font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2';
  
  const variantClasses = {
    primary: 'bg-teal-600 text-white hover:bg-teal-700 focus:ring-teal-500 disabled:bg-teal-400',
    secondary: 'bg-gray-200 text-gray-900 hover:bg-gray-300 focus:ring-gray-500 disabled:bg-gray-100',
    link: 'text-teal-600 hover:text-teal-700 underline',
  }[variant];

  return (
    <button
      type={type}
      disabled={disabled || isLoading}
      className={`${baseClasses} ${variantClasses} ${className}`}
      {...props}
    >
      {isLoading ? (
        <span className="flex items-center justify-center gap-2">
          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
          Chargement...
        </span>
      ) : (
        children
      )}
    </button>
  );
};
